import { useState } from 'react';
import api from '../../hooks/useApi';

// ── Snoozing an action ───────────────────────────────────────────────────────
// Hides an action from your own queue until tomorrow or next week (the coming
// Monday). It is personal: on a group action only the person who snoozed it
// stops seeing it, and everybody else on it carries on as before. The server
// decides the date (POST /actions/:id/snooze); the dates worked out here only
// label the choices.

const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function snoozeChoices(now = new Date()) {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (((8 - now.getDay()) % 7) || 7));
  return [
    { id: 'tomorrow', label: 'Until tomorrow', date: localISO(tomorrow) },
    { id: 'next_week', label: 'Until next week', date: localISO(monday) },
  ];
}

// "Wed 14 Oct" or, with long, "Wednesday 14 October".
export function snoozeDateLabel(iso, long = false) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-GB', long
    ? { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
    : { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

// Still hidden today? The action shows again on the morning of the date.
export function isSnoozed(until, now = new Date()) {
  return !!until && until > localISO(now);
}

export async function setSnooze(actionId, until) {
  const r = await api.post(`actions/${actionId}/snooze`, { until });
  return r?.snoozed_until ?? null;
}

// The 💤 Snooze control: a button that opens the two choices in place. Calls
// onSnoozed(date) with the date the server set.
export function SnoozeButton({ actionId, disabled, onSnoozed }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const choose = async (choice) => {
    setBusy(true); setError('');
    try {
      const until = await setSnooze(actionId, choice);
      setOpen(false);
      onSnoozed && onSnoozed(until);
    } catch (err) {
      setError(err?.message || 'Snoozing failed. Please try again.');
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={e => { e.stopPropagation(); setOpen(true); }} disabled={disabled}
        title="Hide this from your own list for a while. Everyone else on it still sees it."
        className="px-1.5 py-0.5 text-[8px] font-semibold border border-gray-200 text-gray-500 bg-white rounded hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap">💤 Snooze</button>
    );
  }
  return (
    <div className="flex flex-col items-end gap-1 max-w-[180px] text-right" onClick={e => e.stopPropagation()}>
      <span className="text-[8px] text-gray-500 leading-tight">Hide it from your list until:</span>
      {snoozeChoices().map(c => (
        <button key={c.id} onClick={() => choose(c.id)} disabled={busy}
          className="px-1.5 py-0.5 text-[8px] font-semibold border border-violet-200 text-violet-700 bg-violet-50 rounded hover:bg-violet-100 disabled:opacity-40 whitespace-nowrap">
          {c.label} ({snoozeDateLabel(c.date)})
        </button>
      ))}
      <button onClick={() => { setOpen(false); setError(''); }} disabled={busy}
        className="px-1.5 py-0.5 text-[8px] font-semibold border border-gray-200 rounded text-gray-500 hover:bg-gray-50">Keep it showing</button>
      {error && <span className="text-[8px] text-red-600 leading-tight">{error}</span>}
    </div>
  );
}
